

import { useStore } from 'vuex'
import {
  defineComponent,
  computed,
  PropType,
  ref,
  onMounted,
  watch
} from 'vue'

import { $castArray, $setTo, debounce } from '@/utils'
import { Entry } from '@Protium/vue-ozone-utils'
import { SelectOption } from '@/models/generics'
import _ from 'lodash'
import { titleCase } from '@/utils/template'
import { ContentType } from '@/services/directr'
import { CtCategory } from '@/models/interfaces/content-type'

export default defineComponent({
  name: 'SearchableMultiSelect',
  props: {
    modelValue: {
      type: Object as PropType<Array<string>>
    },
    contentType: {
      type: String,
      default: 'id'
    },
    query: {
      type: Function as PropType<(query: string) => Array<{ field: string, method?: string, match: string | number | boolean }>>,
    },
    dialogButton: {
      type: String,
    },
    matchAny: {
      type: Boolean
    },
    createable: {
      type: Boolean
    },
    handlers: {
      type: Object as PropType<{ create(data: { type: string, name: string, label: string }): Promise<Entry> }>
    },
    type: {
      type: String,
      default: 'multi'
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
    },
  },
  emits: ['update:modelValue', 'options', 'search', 'cache', 'select'],
  setup (props, { emit }) {
    const $store = useStore()
    const $directr = $store.getters['Services/Directr']

    const activeCache = ref<Array<Entry>>([])
    const activeOptions = ref<Array<SelectOption>>([])
    const queryString = ref('')
    const loading = ref(false)

    onMounted(async () => {
      await fetch()
      query()
    })

    function updateQueryString (e) {
      if (e === null) queryString.value = ''
      else queryString.value = queryString.value.concat(e)

      emit('search', queryString.value)
    }

    async function fetch () {
      if (!props.modelValue?.length) return
      updateCache(await $directr.get('id', [...props.modelValue]).then(({ items }) => items))
    }

    function updateCache (items: Array<Entry>) {
      $setTo(activeCache.value, items)
      emit('cache', activeCache.value)
    }

    const query = debounce(async () => {
      try {
        loading.value = true
        const q = props.query ? props.query(queryString.value) : [{ field: 'name', method: 'like', match: queryString.value }, { field: 'id', method: 'like', match: queryString.value }]

        const query = (await $directr.get(props.contentType, q, { limit: 5, ...(props.matchAny || !props.query ? { query: { any: true } } : {}) })
          .then(s => {
            updateCache(s.items)
            return s.items
          })).map((item, index) => ({
            label: item.title || item.label || item.name || item.id,
            value: item.sys.id
          }))

        activeOptions.value = query
        emit('options', activeOptions.value)

        return query
      } catch (e) {
        console.warn('search query error')
        throw e
      } finally {
        loading.value = false
      }
    }, 200)

    function update (value) {
      if (!value.length) emit('update:modelValue', value.value, activeCache.value)
      else emit('update:modelValue', value?.map(v => v.value), activeCache.value)
    }

    async function handleCreate (input) {
      // console.log('handleCreate', input)

      if (!props.contentType || props.contentType === 'id') return console.warn(`SearchSelect Tried to create a new entry, but the set ContentType was ${String(props.contentType)}`)
      const name = _.kebabCase(input)
      const label = titleCase(name)
      // check activeOptions.value if the kebab-cased name does not closely match any existing category
      const match = activeOptions.value.findIndex(option => _.kebabCase(option.value) === name || _.kebabCase(option.label) === name)

      // if match, add to selection
      if (match >= 0) return
      if (!props.handlers || !props.handlers.create || typeof props.handlers.create !== 'function') throw Error('SearchSelect tried to create a new entry, but no create handler was set on props.handlers')

      const entry = await props.handlers.create({ type: props.contentType, name, label })

      if (!entry) throw Error(`SearchSelect failed to add entry for input "${input}". The handler response was undefined`)

      // add the option
      activeOptions.value.push({ label, value: entry.sys.id })

      // emit the update
      emit('update:modelValue', (props.modelValue || []).concat(entry.sys.id), activeCache.value)
    }

    watch(() => queryString.value, query)
    watch(() => props.modelValue, () => {
      debounce(fetch, 500)
    })

    return {
      // FIXME does not match
      activeItems: computed(() => {
        return (props.modelValue || [])
          .map(id => activeCache.value?.find(c => c.sys?.id === id) || activeOptions.value?.find(o => o.value === id))
          .filter(Boolean)
          .map((item: any) => ({ label: item.title || item.label || item.name || item.id, value: item.sys?.id || ('label' in item ? item.value : undefined) }))
      }),
      activeCache,
      handleCreate,
      activeOptions,
      updateQueryString,
      queryString,
      emit,
      loading,
      update
    }
  }
})
